import { useEffect, useRef, useState } from 'react';
import { Building2, Plus, User, X } from 'lucide-react';
import {
  prospectsApi,
  type ContactSearchResult,
  type DuplicatesResult,
  type InseeResult,
  type ProspectDetail,
  type ProspectType,
} from '@/services/prospects.api';
import { ORIGIN_VISUAL, ORIGIN_ORDER, type OpportunityOrigin } from '@/services/opportunities.api';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';
import { Banner } from '@/components/ui/Banner';
import { useToast } from '@/components/ui/Toast';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useHotkey } from '@/hooks/useHotkey';
import { legalFormShort, legalFormLong } from '../../shared/legal-forms';
import { ContactFormModal, type ContactDraft } from '@/components/ContactFormModal';

// Entrée de la liste de contacts en attente avant création du prospect.
type PendingContact =
  | { kind: 'new'; draft: ContactDraft }
  | { kind: 'attach'; contact: ContactSearchResult };

function pendingName(p: PendingContact): string {
  return p.kind === 'new'
    ? `${p.draft.firstName} ${p.draft.lastName}`
    : `${p.contact.firstName} ${p.contact.lastName}`;
}

function pendingEmail(p: PendingContact): string {
  return (p.kind === 'new' ? p.draft.email : (p.contact.email ?? '')) || '';
}

interface Props {
  onClose: () => void;
  onCreated: (prospect: ProspectDetail) => void;
  /**
   * Niveau z-index du backdrop. Par défaut `z-50`. Passer `z-[60]` (ou plus)
   * quand la modale est ouverte par-dessus une autre - ex. création d'un
   * client depuis la modale de création d'opportunité.
   */
  zClass?: string;
}

interface CompanyForm {
  companyName: string;
  /** Code INSEE (ex: "5499") si INSEE a répondu. `null` = saisie manuelle. */
  legalFormCode: string | null;
  /** Texte libre quand INSEE absent (fallback). Ignoré si `legalFormCode` est posé. */
  legalFormManual: string;
  nafCode: string;
  /** Libellé NAF issu de fabrique.social.gouv.fr (non affiché dans la modale,
   *  stocké pour apparaître dans l'en-tête fiche prospect). */
  nafLabel: string | null;
  /** Code IDCC (4 chiffres) issu de fabrique.social.gouv.fr. */
  idccCode: string | null;
  /** Libellé convention collective (fabrique.social.gouv.fr si dispo, fallback INSEE). */
  collectiveAgreementLabel: string | null;
  /** Date de création format YYYY-MM-DD ; éditable manuellement en fallback. */
  creationDate: string;
  /** Téléphone de l'entreprise (standard). Saisie manuelle. */
  phone: string;
  addressStreet: string;
  addressCity: string;
  addressPostalCode: string;
}

interface IndividualForm {
  civility: 'M' | 'Mme';
  firstName: string;
  lastName: string;
  birthDate: string;
  personalEmail: string;
  personalPhone: string;
  isPPE: boolean;
  addressStreet: string;
  addressCity: string;
  addressPostalCode: string;
}

const EMPTY_COMPANY: CompanyForm = {
  companyName: '',
  legalFormCode: null,
  legalFormManual: '',
  nafCode: '',
  nafLabel: null,
  idccCode: null,
  collectiveAgreementLabel: null,
  creationDate: '',
  phone: '',
  addressStreet: '',
  addressCity: '',
  addressPostalCode: '',
};

const EMPTY_INDIVIDUAL: IndividualForm = {
  civility: 'M',
  firstName: '',
  lastName: '',
  birthDate: '',
  personalEmail: '',
  personalPhone: '',
  isPPE: false,
  addressStreet: '',
  addressCity: '',
  addressPostalCode: '',
};

export function CreateProspectModal({ onClose, onCreated, zClass = 'z-50' }: Props) {
  const user = useAuthStore((s) => s.user)!;
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();

  const [type, setType] = useState<ProspectType>('company');
  const [siret, setSiret] = useState('');
  const [companyForm, setCompanyForm] = useState<CompanyForm>(EMPTY_COMPANY);
  const [individualForm, setIndividualForm] = useState<IndividualForm>(EMPTY_INDIVIDUAL);
  // Refonte 2026-05-23 : `origin` (canal d'acquisition) déplacé depuis
  // Opportunity vers Prospect. Champ optionnel à la création — peut être
  // saisi plus tard via la fiche prospect.
  const [origin, setOrigin] = useState<OpportunityOrigin | ''>('');
  const [inseeLoading, setInseeLoading] = useState(false);
  const [inseeError, setInseeError] = useState<string | null>(null);
  const [duplicates, setDuplicates] = useState<DuplicatesResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Contacts mis en mémoire LOCAL avant la création du prospect. Applicable
  // uniquement aux prospects entreprise. Deux natures (modèle n:m) :
  //  - `new`    : nouvelle personne à créer puis rattacher,
  //  - `attach` : contact EXISTANT à rattacher (point 2).
  // À la validation finale, on crée le prospect puis on persiste chaque entrée
  // en séquentiel. Échec → toast rouge mais le prospect reste créé (stratégie
  // A validée 2026-04-24).
  const [pendingContacts, setPendingContacts] = useState<PendingContact[]>([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);

  // Focus trap + escape désactivés quand la modale contact imbriquée est
  // ouverte : sinon l'Escape du parent fermerait les 2, et le focus trap
  // parent capturerait les inputs de la modale enfant.
  useFocusTrap(!contactModalOpen, dialogRef);
  useHotkey('escape', onClose, { allowInInput: true, enabled: !contactModalOpen });

  // --- Effet INSEE pour entreprise (SIRET uniquement) ---
  // IMPORTANT : ne dépend QUE du SIRET (pas de companyName). Sinon, éditer la
  // raison sociale re-déclencherait l'appel INSEE qui ré-écraserait le champ
  // avec la raison sociale d'origine → impossible de personnaliser le nom. Le
  // lookup ne se relance donc qu'au changement de SIRET ; la raison sociale
  // pré-remplie reste librement modifiable ensuite.
  useEffect(() => {
    if (type !== 'company') {
      setInseeError(null);
      return;
    }
    const clean = siret.replace(/\s/g, '');
    if (!/^\d{14}$/.test(clean)) return;
    let cancelled = false;
    setInseeError(null);

    void (async () => {
      setInseeLoading(true);
      try {
        const res: InseeResult = await prospectsApi.insee(token, clean);
        if (cancelled) return;
        if (res.data) {
          setCompanyForm((prev) => ({
            ...prev,
            companyName: res.data!.companyName,
            legalFormCode: res.data!.legalForm ?? null,
            legalFormManual: '',
            nafCode: res.data!.nafCode ?? '',
            nafLabel: res.data!.nafLabel,
            idccCode: res.data!.idccCode,
            collectiveAgreementLabel: res.data!.collectiveAgreement,
            // INSEE retourne du ISO avec time ; on normalise en YYYY-MM-DD pour
            // alimenter l'input type="date".
            creationDate: res.data!.creationDate ? res.data!.creationDate.slice(0, 10) : '',
            addressStreet: res.data!.addressStreet ?? '',
            addressCity: res.data!.addressCity ?? '',
            addressPostalCode: res.data!.addressPostalCode ?? '',
          }));
        } else {
          setInseeError('SIRET inconnu chez INSEE - saisie manuelle requise.');
        }
      } catch {
        if (!cancelled) setInseeError('INSEE indisponible - saisie manuelle.');
      } finally {
        if (!cancelled) setInseeLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [type, siret, token]);

  // --- Effet dédupe pour entreprise (SIRET + raison sociale) ---
  // Séparé de l'INSEE : la dédupe DOIT se rafraîchir quand l'utilisateur édite
  // la raison sociale (similarité non bloquante), mais sans relancer l'INSEE.
  useEffect(() => {
    if (type !== 'company') {
      setDuplicates(null);
      return;
    }
    const clean = siret.replace(/\s/g, '');
    if (!/^\d{14}$/.test(clean)) {
      setDuplicates(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const dup = await prospectsApi.checkDuplicates(token, {
          siret: clean,
          company_name: companyForm.companyName || undefined,
        });
        if (!cancelled) setDuplicates(dup);
      } catch {
        /* non-blocant */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [type, siret, token, companyForm.companyName]);

  // --- Effet dédupe pour particulier (triplet nom+prénom+DDN) ---
  useEffect(() => {
    if (type !== 'individual') return;
    const { firstName, lastName, birthDate } = individualForm;
    if (!firstName || !lastName || !birthDate) {
      setDuplicates(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const dup = await prospectsApi.checkDuplicates(token, {
          first_name: firstName,
          last_name: lastName,
          birth_date: birthDate,
        });
        if (!cancelled) setDuplicates(dup);
      } catch {
        /* non-blocant */
      }
    })();
    return () => {
      cancelled = true;
    };
    // Seuls les 3 champs d'identité déclenchent un re-check — personalEmail,
    // adresse, etc. ne sont pas discriminants. eslint-disable pour éviter
    // d'ajouter individualForm entier et déclencher une requête inutile
    // à chaque frappe de caractère dans un autre champ.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, individualForm.firstName, individualForm.lastName, individualForm.birthDate, token]);

  function handleTypeChange(next: ProspectType) {
    setType(next);
    // Reset cross-type pour éviter des états fantômes à la soumission.
    setSiret('');
    setCompanyForm(EMPTY_COMPANY);
    setIndividualForm(EMPTY_INDIVIDUAL);
    setDuplicates(null);
    setInseeError(null);
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user.teamId) {
      setError('Aucune équipe associée à votre compte - contactez un administrateur.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const commonFields = {
        assignedTeamId: user.teamId,
        assignedUserId: user.id,
        // Refonte 2026-05-23 : origin appliqué aux 2 branches (company / individual)
        // via commonFields. Omis du payload si vide → null côté DB.
        ...(origin ? { origin } : {}),
      };
      let payload: Record<string, unknown>;
      if (type === 'company') {
        // Forme juridique : on préfère le code INSEE canonique (stockage
        // durable + dérivation label au display). Si vide (INSEE absent ou
        // reset), on retombe sur le texte libre manuel.
        const legalFormValue = companyForm.legalFormCode ?? companyForm.legalFormManual.trim();
        payload = {
          type: 'company',
          siret: siret.replace(/\s/g, ''),
          companyName: companyForm.companyName,
          ...(legalFormValue ? { legalForm: legalFormValue } : {}),
          ...(companyForm.nafCode ? { nafCode: companyForm.nafCode } : {}),
          ...(companyForm.nafLabel ? { nafLabel: companyForm.nafLabel } : {}),
          ...(companyForm.idccCode ? { idccCode: companyForm.idccCode } : {}),
          ...(companyForm.collectiveAgreementLabel
            ? { collectiveAgreement: companyForm.collectiveAgreementLabel }
            : {}),
          ...(companyForm.creationDate
            ? { creationDate: new Date(`${companyForm.creationDate}T00:00:00Z`).toISOString() }
            : {}),
          ...(companyForm.phone.trim() ? { phone: companyForm.phone.trim() } : {}),
          ...(companyForm.addressStreet ? { addressStreet: companyForm.addressStreet } : {}),
          ...(companyForm.addressCity ? { addressCity: companyForm.addressCity } : {}),
          ...(companyForm.addressPostalCode
            ? { addressPostalCode: companyForm.addressPostalCode }
            : {}),
          ...commonFields,
        };
      } else {
        payload = {
          type: 'individual',
          civility: individualForm.civility,
          firstName: individualForm.firstName,
          lastName: individualForm.lastName,
          birthDate: individualForm.birthDate,
          isPPE: individualForm.isPPE,
          ...(individualForm.personalEmail ? { personalEmail: individualForm.personalEmail } : {}),
          ...(individualForm.personalPhone ? { personalPhone: individualForm.personalPhone } : {}),
          ...(individualForm.addressStreet ? { addressStreet: individualForm.addressStreet } : {}),
          ...(individualForm.addressCity ? { addressCity: individualForm.addressCity } : {}),
          ...(individualForm.addressPostalCode
            ? { addressPostalCode: individualForm.addressPostalCode }
            : {}),
          ...commonFields,
        };
      }
      const created = await prospectsApi.create(token, payload);

      // Création des contacts en attente (entreprise uniquement ; particuliers
      // n'en portent pas). Stratégie A : POST séquentiel, un échec → toast
      // rouge par contact fautif, le prospect reste créé.
      if (type === 'company' && pendingContacts.length > 0) {
        const failures: Array<{ name: string; reason: string }> = [];
        for (const p of pendingContacts) {
          try {
            if (p.kind === 'new') {
              const c = p.draft;
              await prospectsApi.contacts.create(token, created.id, {
                firstName: c.firstName,
                lastName: c.lastName,
                role: c.role,
                ...(c.email ? { email: c.email } : {}),
                ...(c.phoneMobile ? { phoneMobile: c.phoneMobile } : {}),
                ...(c.phoneLandline ? { phoneLandline: c.phoneLandline } : {}),
                isPrimary: c.isPrimary,
                observations: c.observations || null,
              });
            } else {
              await prospectsApi.contacts.attach(token, created.id, {
                contactId: p.contact.id,
              });
            }
          } catch (err) {
            failures.push({
              name: pendingName(p),
              reason: err instanceof Error ? err.message : 'erreur inconnue',
            });
          }
        }
        if (failures.length > 0) {
          toast({
            tone: 'danger',
            message: `Client créé, mais ${failures.length} contact(s) n'ont pas pu être ajoutés : ${failures
              .map((f) => `${f.name} (${f.reason})`)
              .join(', ')}. Vous pouvez les recréer depuis la fiche.`,
          });
        }
      }

      onCreated(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit =
    type === 'company'
      ? !submitting &&
        !!companyForm.companyName &&
        /^\d{14}$/.test(siret.replace(/\s/g, '')) &&
        !duplicates?.exact
      : !submitting &&
        !!individualForm.firstName &&
        !!individualForm.lastName &&
        !!individualForm.birthDate &&
        !duplicates?.exact;

  return (
    <div
      className={`fixed inset-0 ${zClass} flex items-center justify-center bg-brand/30 px-4 backdrop-blur-sm animate-fade-in`}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Créer un prospect"
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg bg-white shadow-modal animate-scale-in"
      >
        <form onSubmit={handleSubmit} className="space-y-5 p-6">
          <div className="flex items-center justify-between border-b border-border-subtle pb-4">
            <div>
              <h2 className="text-lg font-semibold tracking-tight text-brand">Nouveau prospect</h2>
              <p className="mt-0.5 text-xs text-text-muted">
                {type === 'company'
                  ? "Le SIRET déclenche l'autocomplétion INSEE et la vérification des doublons."
                  : 'Un particulier est identifié par son prénom, son nom et sa date de naissance.'}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-text-muted hover:bg-gray-100 hover:text-brand"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Toggle type entreprise / particulier */}
          <div className="flex gap-2 rounded-md bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => handleTypeChange('company')}
              className={`flex flex-1 items-center justify-center gap-2 rounded px-3 py-1.5 text-sm font-medium transition ${
                type === 'company'
                  ? 'bg-white text-brand shadow-sm'
                  : 'text-text-muted hover:text-brand'
              }`}
              aria-pressed={type === 'company'}
            >
              <Building2 className="h-4 w-4" />
              Entreprise
            </button>
            <button
              type="button"
              onClick={() => handleTypeChange('individual')}
              className={`flex flex-1 items-center justify-center gap-2 rounded px-3 py-1.5 text-sm font-medium transition ${
                type === 'individual'
                  ? 'bg-white text-brand shadow-sm'
                  : 'text-text-muted hover:text-brand'
              }`}
              aria-pressed={type === 'individual'}
            >
              <User className="h-4 w-4" />
              Particulier
            </button>
          </div>

          {type === 'company' ? (
            <CompanyFields
              siret={siret}
              onSiretChange={setSiret}
              form={companyForm}
              onFormChange={setCompanyForm}
              inseeLoading={inseeLoading}
              inseeError={inseeError}
              duplicates={duplicates}
            />
          ) : (
            <IndividualFields
              form={individualForm}
              onFormChange={setIndividualForm}
              duplicates={duplicates}
            />
          )}

          {/* Refonte 2026-05-23 : champ Origine (canal d'acquisition) déplacé
              depuis Opportunity vers Prospect. Optionnel à la création, peut
              être renseigné ou modifié plus tard sur la fiche prospect. */}
          <div className="border-t border-border-subtle pt-4">
            <Label htmlFor="prospect-origin">
              Origine <span className="text-text-muted text-xs">(optionnel)</span>
            </Label>
            <select
              id="prospect-origin"
              value={origin}
              onChange={(e) => setOrigin(e.target.value as OpportunityOrigin | '')}
              className="border-border-subtle text-text-primary focus:border-accent focus:ring-accent block w-full rounded-md border bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1"
            >
              <option value="">— Non renseignée —</option>
              {ORIGIN_ORDER.map((code) => (
                <option key={code} value={code}>
                  {ORIGIN_VISUAL[code].label}
                </option>
              ))}
            </select>
            <p className="text-text-muted mt-1 text-xs">
              Par quel canal avez-vous connu ce client&nbsp;? (modifiable plus tard)
            </p>
          </div>

          {/* Contacts imbriqués (entreprise uniquement) - les contacts sont
              empilés en mémoire ici et persistés après la création du prospect
              (stratégie A : échec contact → toast, prospect conservé). */}
          {type === 'company' && (
            <div className="border-t border-border-subtle pt-4">
              <div className="mb-2 flex items-center justify-between">
                <Label>Contacts ({pendingContacts.length})</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  leadingIcon={<Plus className="h-3.5 w-3.5" />}
                  onClick={() => setContactModalOpen(true)}
                >
                  Ajouter un contact
                </Button>
              </div>
              {pendingContacts.length > 0 && (
                <ul className="space-y-1.5">
                  {pendingContacts.map((p, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between rounded-md border border-border-subtle bg-gray-50 px-3 py-2 text-sm"
                    >
                      <div className="min-w-0">
                        <div className="truncate font-medium text-brand">
                          {pendingName(p)}
                          {p.kind === 'new' && p.draft.isPrimary && (
                            <span className="ml-2 inline-flex items-center rounded-full bg-accent-50 px-2 py-0.5 text-[10px] font-semibold text-accent-600">
                              Principal
                            </span>
                          )}
                          {p.kind === 'attach' && (
                            <span className="ml-2 inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold text-text-muted">
                              Existant
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 truncate text-xs text-text-muted">
                          {pendingEmail(p) || '—'}
                        </div>
                      </div>
                      <button
                        type="button"
                        aria-label={`Retirer ${pendingName(p)}`}
                        onClick={() =>
                          setPendingContacts((prev) => prev.filter((_, idx) => idx !== i))
                        }
                        className="ml-2 shrink-0 rounded-md p-1 text-text-muted hover:bg-gray-100 hover:text-danger-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {error && <Banner tone="danger">{error}</Banner>}

          <div className="flex justify-end gap-2 border-t border-border-subtle pt-4">
            <Button variant="secondary" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" variant="primary" loading={submitting} disabled={!canSubmit}>
              {submitting ? 'Création…' : 'Créer'}
            </Button>
          </div>
        </form>
      </div>

      {contactModalOpen && (
        <ContactFormModal
          existingEmails={pendingContacts.map(pendingEmail).filter(Boolean)}
          zClass="z-[55]"
          // Recherche d'attache (point 2) : le prospect n'existe pas encore, on
          // ne passe pas excludeProspectId mais on filtre côté client les
          // contacts déjà empilés en attente.
          searchExisting={async (q) => {
            const alreadyPending = new Set(
              pendingContacts.filter((p) => p.kind === 'attach').map((p) => p.contact.id),
            );
            const res = await prospectsApi.contacts.search(token, q);
            return res.filter((c) => !alreadyPending.has(c.id));
          }}
          onAttachExisting={(contact) => {
            setPendingContacts((prev) => [...prev, { kind: 'attach', contact }]);
            setContactModalOpen(false);
          }}
          onClose={() => setContactModalOpen(false)}
          onSubmit={async (draft) => {
            setPendingContacts((prev) => [...prev, { kind: 'new', draft }]);
            setContactModalOpen(false);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sous-composant entreprise
// ---------------------------------------------------------------------------
function CompanyFields({
  siret,
  onSiretChange,
  form,
  onFormChange,
  inseeLoading,
  inseeError,
  duplicates,
}: {
  siret: string;
  onSiretChange: (v: string) => void;
  form: CompanyForm;
  onFormChange: (updater: (prev: CompanyForm) => CompanyForm) => void;
  inseeLoading: boolean;
  inseeError: string | null;
  duplicates: DuplicatesResult | null;
}) {
  return (
    <>
      <div>
        <Label htmlFor="siret" required>
          SIRET
        </Label>
        <Input
          id="siret"
          type="text"
          inputMode="numeric"
          value={siret}
          onChange={(e) => onSiretChange(e.target.value.replace(/[^\d]/g, ''))}
          maxLength={14}
          placeholder="14 chiffres"
          className="font-mono"
          required
        />
        {inseeLoading && <p className="mt-1 text-xs text-text-muted">Recherche INSEE…</p>}
        {inseeError && <p className="mt-1 text-xs text-warning-600">{inseeError}</p>}
      </div>

      {duplicates?.exact && (
        <Banner tone="danger" title="SIRET déjà en base">
          « {duplicates.exact.displayName} » - assigné à{' '}
          {duplicates.exact.assignedUserEmail ?? 'un collègue'}. Création impossible.
        </Banner>
      )}

      {duplicates && duplicates.similar.length > 0 && !duplicates.exact && (
        <Banner tone="warning" title="Raisons sociales proches déjà présentes">
          <ul className="mt-1 list-disc pl-4">
            {duplicates.similar.slice(0, 3).map((s) => (
              <li key={s.id}>
                {s.displayName}
                {s.siret ? ` (SIRET ${s.siret},` : ' ('}similarité {(s.score * 100).toFixed(0)}%)
              </li>
            ))}
          </ul>
        </Banner>
      )}

      <div>
        <Label htmlFor="companyName" required>
          Raison sociale
        </Label>
        <Input
          id="companyName"
          value={form.companyName}
          onChange={(e) => onFormChange((p) => ({ ...p, companyName: e.target.value }))}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="legalForm">Forme juridique</Label>
          {form.legalFormCode ? (
            // INSEE a répondu : on affiche le label court (dérivé via la table
            // statique) en lecture seule, avec tooltip sur le label long si
            // connu. Le code canonique est stocké en state et envoyé au submit.
            <div
              className="mt-1 flex h-10 items-center rounded-md border border-border bg-gray-50 px-3 text-sm text-brand"
              title={legalFormLong(form.legalFormCode) ?? undefined}
            >
              {legalFormShort(form.legalFormCode)}
            </div>
          ) : (
            <Input
              id="legalForm"
              value={form.legalFormManual}
              onChange={(e) => onFormChange((p) => ({ ...p, legalFormManual: e.target.value }))}
              placeholder="SARL, SAS, etc."
            />
          )}
        </div>
        <div>
          <Label htmlFor="nafCode">Code NAF</Label>
          <Input
            id="nafCode"
            value={form.nafCode}
            onChange={(e) => onFormChange((p) => ({ ...p, nafCode: e.target.value }))}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="creationDate">Date de création</Label>
          <Input
            id="creationDate"
            type="date"
            value={form.creationDate}
            onChange={(e) => onFormChange((p) => ({ ...p, creationDate: e.target.value }))}
            max={new Date().toISOString().slice(0, 10)}
          />
        </div>
        <div>
          <Label>Code IDCC</Label>
          <div className="mt-1 flex h-10 items-center rounded-md border border-border bg-gray-50 px-3 font-mono text-sm text-brand">
            {form.idccCode ?? <span className="text-text-muted">—</span>}
          </div>
        </div>
      </div>

      <div>
        <Label htmlFor="company-phone">Téléphone</Label>
        <Input
          id="company-phone"
          type="tel"
          value={form.phone}
          onChange={(e) => onFormChange((p) => ({ ...p, phone: e.target.value }))}
          placeholder="01 23 45 67 89"
        />
      </div>

      {form.collectiveAgreementLabel && (
        <div>
          <Label>Convention collective</Label>
          <div className="mt-1 rounded-md border border-border bg-gray-50 px-3 py-2 text-sm text-brand">
            {form.idccCode
              ? `${form.idccCode} — ${form.collectiveAgreementLabel}`
              : form.collectiveAgreementLabel}
          </div>
        </div>
      )}

      <AddressBlock
        street={form.addressStreet}
        city={form.addressCity}
        postalCode={form.addressPostalCode}
        onChange={(patch) => onFormChange((p) => ({ ...p, ...patch }))}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Sous-composant particulier
// ---------------------------------------------------------------------------
function IndividualFields({
  form,
  onFormChange,
  duplicates,
}: {
  form: IndividualForm;
  onFormChange: (updater: (prev: IndividualForm) => IndividualForm) => void;
  duplicates: DuplicatesResult | null;
}) {
  return (
    <>
      {duplicates?.exact && (
        <Banner tone="danger" title="Ce particulier existe déjà">
          « {duplicates.exact.displayName} ». Création impossible.
        </Banner>
      )}
      {duplicates && duplicates.similar.length > 0 && !duplicates.exact && (
        <Banner tone="warning" title="Homonymes déjà en base">
          <ul className="mt-1 list-disc pl-4">
            {duplicates.similar.slice(0, 3).map((s) => (
              <li key={s.id}>
                {s.displayName} (similarité {(s.score * 100).toFixed(0)}%)
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-text-muted">
            Les homonymes sont fréquents - continuez si la date de naissance diffère.
          </p>
        </Banner>
      )}

      <div className="grid grid-cols-[120px_1fr_1fr] gap-3">
        <div>
          <Label htmlFor="civility" required>
            Civilité
          </Label>
          <select
            id="civility"
            value={form.civility}
            onChange={(e) =>
              onFormChange((p) => ({ ...p, civility: e.target.value as 'M' | 'Mme' }))
            }
            className="mt-1 block h-10 w-full rounded-md border border-border bg-white px-3 text-sm text-brand focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
            required
          >
            <option value="M">M.</option>
            <option value="Mme">Mme</option>
          </select>
        </div>
        <div>
          <Label htmlFor="firstName" required>
            Prénom
          </Label>
          <Input
            id="firstName"
            value={form.firstName}
            onChange={(e) => onFormChange((p) => ({ ...p, firstName: e.target.value }))}
            required
          />
        </div>
        <div>
          <Label htmlFor="lastName" required>
            Nom
          </Label>
          <Input
            id="lastName"
            value={form.lastName}
            onChange={(e) => onFormChange((p) => ({ ...p, lastName: e.target.value }))}
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="birthDate" required>
            Date de naissance
          </Label>
          <Input
            id="birthDate"
            type="date"
            value={form.birthDate}
            onChange={(e) => onFormChange((p) => ({ ...p, birthDate: e.target.value }))}
            max={new Date().toISOString().slice(0, 10)}
            required
          />
        </div>
        <div className="flex items-end pb-1">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-brand">
            <input
              type="checkbox"
              checked={form.isPPE}
              onChange={(e) => onFormChange((p) => ({ ...p, isPPE: e.target.checked }))}
              className="h-4 w-4 rounded border-border text-accent focus:ring-accent/30"
            />
            <span>
              PPE (Personne Politiquement Exposée)
              <span className="ml-1 text-xs text-text-muted">- LCB-FT</span>
            </span>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="personalEmail">Email</Label>
          <Input
            id="personalEmail"
            type="email"
            value={form.personalEmail}
            onChange={(e) => onFormChange((p) => ({ ...p, personalEmail: e.target.value }))}
          />
        </div>
        <div>
          <Label htmlFor="personalPhone">Téléphone</Label>
          <Input
            id="personalPhone"
            type="tel"
            value={form.personalPhone}
            onChange={(e) => onFormChange((p) => ({ ...p, personalPhone: e.target.value }))}
            placeholder="06 12 34 56 78"
          />
        </div>
      </div>

      <AddressBlock
        street={form.addressStreet}
        city={form.addressCity}
        postalCode={form.addressPostalCode}
        onChange={(patch) => onFormChange((p) => ({ ...p, ...patch }))}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Adresse (commune aux deux types)
// ---------------------------------------------------------------------------
function AddressBlock({
  street,
  city,
  postalCode,
  onChange,
}: {
  street: string;
  city: string;
  postalCode: string;
  onChange: (patch: {
    addressStreet?: string;
    addressCity?: string;
    addressPostalCode?: string;
  }) => void;
}) {
  return (
    <div>
      <Label htmlFor="street">Adresse</Label>
      <Input
        id="street"
        placeholder="Rue"
        value={street}
        onChange={(e) => onChange({ addressStreet: e.target.value })}
      />
      <div className="mt-2 grid grid-cols-3 gap-2">
        <Input
          placeholder="CP"
          value={postalCode}
          onChange={(e) => onChange({ addressPostalCode: e.target.value })}
        />
        <Input
          className="col-span-2"
          placeholder="Ville"
          value={city}
          onChange={(e) => onChange({ addressCity: e.target.value })}
        />
      </div>
    </div>
  );
}
